/**
 * QL-287 — Add / edit transfer modal of the Cash Transfers component.
 *
 * From account, To account and Amount. The amount is always in the From
 * account's currency, shown in its label ("Amount (EUR)") and never
 * selectable. Nothing is saved while the form is open: "Add transfer" /
 * "Save" writes it, Cancel discards. The server validates again.
 * QL-539: every From option lists its account's available cash, and the
 * selected one repeats it under the select.
 * QL-742: "Use all" fills the amount with that available cash.
 */

import type {
  AccountCashOverviewRow,
  CashAccount,
  CashTransfer,
  CreateCashTransferRequest,
  Translated,
} from "@repo/shared/types";
import { useMemo, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountCashOverview } from "@/lib/hooks/use-flow-instances/use-cash";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { formatNumberInput, parseNumberInput } from "../../financial-situation-widget/utils";
import { availableAmount, CashTransferAvailableCash } from "./cash-transfer-available-cash";
import { T } from "./cash-transfer-form.i18n";
import { type FormatNumberFn, wholeAvailableCash } from "./types";

/**
 * QL-705 — the dialog is wide enough for long account names, and the account
 * lists stay as wide as their select, wrapping a name that is longer still.
 */
const ACCOUNT_LIST_CLASS = "max-w-[var(--radix-select-trigger-width)]";
const ACCOUNT_OPTION_CLASS = "h-auto min-h-8 whitespace-normal wrap-anywhere [&>span]:min-w-0";

export type CashTransferFormProps = {
  instanceId: string;
  /** The transfer being edited; null = add mode. */
  transfer: CashTransfer | null;
  accounts: CashAccount[];
  onClose: () => void;
  onSave: (data: CreateCashTransferRequest) => Promise<{ success: boolean; error?: string }>;
};

/** Option label in the transfer form: "Account #2 · EUR · New account". */
function accountOptionLabel(account: CashAccount, t: Translated<typeof T>): string {
  const parts = [account.name, account.currency];
  if (account.isNew) parts.push(t.newAccount);
  return parts.join(" · ");
}

/** One-line text for a From option: "Available: 4 000 NOK". */
function availableCashSummary(
  account: CashAccount,
  row: AccountCashOverviewRow | undefined,
  fmt: FormatNumberFn,
  t: Translated<typeof T>,
): string {
  return `${t.availableCash}: ${availableAmount(account, row, fmt)}`;
}

export function CashTransferForm({
  instanceId,
  transfer,
  accounts,
  onClose,
  onSave,
}: CashTransferFormProps) {
  const t = useT(T);
  const { formatNumber, parseNumber } = useRegionalFormat();
  // An account the flow no longer offers (its goal was deleted) starts empty,
  // so the select shows its placeholder instead of a blank value.
  const offered = (key: string | undefined) =>
    key && accounts.some((a) => a.key === key) ? key : "";
  const [fromKey, setFromKey] = useState(offered(transfer?.fromAccountKey));
  const [toKey, setToKey] = useState(offered(transfer?.toAccountKey));
  const [amount, setAmount] = useState<number | undefined>(transfer?.amount);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // The projection without the transfer being edited, so its own amount is
  // not counted against the account it moves cash out of.
  const { data: overview, error: overviewError } = useAccountCashOverview(instanceId, {
    excludeTransferId: transfer?.id,
  });
  const rowByKey = useMemo(
    () => new Map(overview?.rows.map((r) => [r.account.key, r])),
    [overview],
  );

  const isEdit = transfer !== null;
  const fromAccount = accounts.find((a) => a.key === fromKey);
  const fromRow = fromAccount && !overviewError ? rowByKey.get(fromAccount.key) : undefined;
  const allAvailable = fromRow ? wholeAvailableCash(fromRow.cashLeft) : 0;
  const amountLabel = fromAccount ? t.amountWithCurrency(fromAccount.currency) : t.amount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromKey) return setError(t.fromRequired);
    if (!toKey) return setError(t.toRequired);
    if (fromKey === toKey) return setError(t.sameAccount);
    if (!amount || amount <= 0) return setError(t.amountRequired);
    setError(null);
    startTransition(async () => {
      const result = await onSave({ fromAccountKey: fromKey, toAccountKey: toKey, amount });
      if (!result.success) {
        setError(result.error || t.failedToSave);
        return;
      }
      onClose();
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !isPending && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? t.editTransfer : t.addTransfer}</DialogTitle>
          <DialogDescription>{t.transferDialogDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cash-transfer-from">{t.fromAccount}</Label>
              <Select value={fromKey} onValueChange={setFromKey} disabled={isPending}>
                <SelectTrigger id="cash-transfer-from">
                  <SelectValue placeholder={t.selectAccount} />
                </SelectTrigger>
                <SelectContent className={ACCOUNT_LIST_CLASS}>
                  {accounts.map((a) => (
                    <SelectItem
                      key={a.key}
                      value={a.key}
                      className={ACCOUNT_OPTION_CLASS}
                      // A failed read says so once, under the select — not "…" on every option.
                      description={
                        overviewError
                          ? undefined
                          : availableCashSummary(a, rowByKey.get(a.key), formatNumber, t)
                      }
                    >
                      {accountOptionLabel(a, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {overviewError ? (
                <p className="text-xs text-muted-foreground">{t.availableCashFailed}</p>
              ) : (
                fromAccount && (
                  <CashTransferAvailableCash
                    account={fromAccount}
                    row={rowByKey.get(fromAccount.key)}
                    fmt={formatNumber}
                  />
                )
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cash-transfer-to">{t.toAccount}</Label>
              <Select value={toKey} onValueChange={setToKey} disabled={isPending}>
                <SelectTrigger id="cash-transfer-to">
                  <SelectValue placeholder={t.selectAccount} />
                </SelectTrigger>
                <SelectContent className={ACCOUNT_LIST_CLASS}>
                  {accounts.map((a) => (
                    <SelectItem
                      key={a.key}
                      value={a.key}
                      disabled={a.key === fromKey}
                      className={ACCOUNT_OPTION_CLASS}
                    >
                      {accountOptionLabel(a, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cash-transfer-amount">{amountLabel}</Label>
              <div className="flex gap-2">
                <Input
                  id="cash-transfer-amount"
                  type="text"
                  inputMode="numeric"
                  value={formatNumberInput(amount, formatNumber)}
                  onChange={(e) => setAmount(parseNumberInput(e.target.value, parseNumber))}
                  disabled={isPending}
                  placeholder="0"
                  className="text-right"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAmount(allAvailable)}
                  disabled={isPending || allAvailable <= 0}
                  data-testid="cash-transfer-use-all"
                >
                  {t.useAll}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {isEdit ? t.save : t.addTransfer}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
